import React, { useEffect, useState } from 'react'
import {Alert, Spinner, Table} from 'react-bootstrap';
import StackedContentContainer from "../ContentContainer";
import {Link, useLocation, useParams} from "react-router-dom";
import {DriverTimeSheet, TimeSheet, UserRoles} from "../../types";
import {checkIsAllowed, getDateString, getTimeString} from "../../util/Helpers";
import {getTimesheets, getDriverTimesheets} from "../../api/dataService";
import {useSelector} from "react-redux";
import {RootState} from "../../redux/reduxTypes";

const DailyTimeCard = () => {
    const location = useLocation();
    const { id } = useParams();
    const [timeEntries, setTimeEntries] = useState<TimeSheet[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const user = useSelector((state: RootState) => state.user);
    const isLabourer = checkIsAllowed(user.groups, [UserRoles.labourer]);
    let totalTimeElapsed = 0;

    useEffect(() => {
        // If we have state from navigation, use it initially
        if (location?.state) {
            console.log("Location state:", location.state);
            const initialEntries = location.state as TimeSheet[];
            // Filter out deleted timesheets
            const filteredEntries = initialEntries.filter(entry => entry.status !== 'deleted');

            // If user is a Labourer, filter to only show their own timesheets
            const userFilteredEntries = isLabourer
                ? filteredEntries.filter(entry => entry.employee && entry.employee.id === user.userid)
                : filteredEntries;

            setTimeEntries(userFilteredEntries);

            // If user is a Labourer and there are no timesheets for them, set an error
            if (isLabourer && userFilteredEntries.length === 0) {
                setError("You have not made any time entries for today.");
            } else {
                setError(null);
            }

            setIsLoading(false);
        }

        // Fetch fresh data regardless
        Promise.all([getTimesheets(), getDriverTimesheets()])
            .then(([timesheetResponse, driverTimesheetResponse]) => {
                let allTimesheets: TimeSheet[] = [];

                if (Array.isArray(timesheetResponse)) {
                    allTimesheets = [...allTimesheets, ...timesheetResponse];
                }

                if (Array.isArray(driverTimesheetResponse)) {
                    allTimesheets = [...allTimesheets, ...driverTimesheetResponse];
                }

                // Filter out deleted timesheets
                const filteredTimesheets = allTimesheets.filter(entry => entry.status !== 'deleted');

                // If user is a Labourer, filter to only show their own timesheets
                const userFilteredTimesheets = isLabourer
                    ? filteredTimesheets.filter(entry => entry.employee && entry.employee.id === user.userid)
                    : filteredTimesheets;

                // If we have an ID, filter to only show timesheets for that day
                if (id) {
                    const dayIndex = parseInt(id);
                    const today = new Date();
                    const targetDate = new Date(today);
                    targetDate.setDate(today.getDate() - dayIndex);
                    const targetDateString = targetDate.toLocaleDateString();

                    const timesheetsForDay = userFilteredTimesheets.filter(entry => {
                        const entryDate = new Date(entry.start_date);
                        return entryDate.toLocaleDateString() === targetDateString;
                    });

                    setTimeEntries(timesheetsForDay);

                    // If user is a Labourer and there are no timesheets for them, set an error
                    if (isLabourer && timesheetsForDay.length === 0) {
                        setError("You have not made any time entries for today.");
                    } else {
                        setError(null);
                    }
                } else {
                    setTimeEntries(userFilteredTimesheets);
                }

                setIsLoading(false);
            })
            .catch(error => {
                console.error("Error fetching timesheets:", error);
                setIsLoading(false);
            });
    }, [id, location.state, user.username, isLabourer]);

    // Check if any entries are driver time sheets
    const hasDriverTimeSheets = timeEntries.some(entry => 'vehicle' in entry);

    // Filter out any deleted timesheets before calculating total hours
    const nonDeletedEntries = timeEntries.filter(entry => entry.status !== 'deleted');
    const sortedEntries = [...nonDeletedEntries].sort((a, b) => {
        const ta = new Date(a.start_date).getTime();
        const tb = new Date(b.start_date).getTime();
        if (isNaN(ta) && isNaN(tb)) return 0;
        if (isNaN(ta)) return 1;
        if (isNaN(tb)) return -1;
        return ta - tb;
    });

    for (const timeSheet of nonDeletedEntries) {
      const startDate = new Date(timeSheet.start_date);
      const endDate = new Date(timeSheet.end_date);
      const timeElapsedMs = Number(endDate)-Number(startDate);
      totalTimeElapsed += timeElapsedMs;
    }
  const totalHoursWorked = totalTimeElapsed / (1000 * 60 * 60); // hours
  if (isLoading) {
    return (
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );
  }

  return(
        <StackedContentContainer title={''}>
          {error && <Alert variant="warning">{error}</Alert>}
          <h5>Total Hours: {totalHoursWorked}</h5>
          <Table responsive>
            <thead>
                <tr>
                    <th>Event</th>
                    <th>Date</th>
                    <th>Time In</th>
                    <th>Time Out</th>
                    {hasDriverTimeSheets && (
                      <>
                        <th>Vehicle</th>
                        <th>Drive Time To</th>
                        <th>Drive Time From</th>
                        <th>Off Truck Time</th>
                      </>
                    )}
                    <th>Employee</th>
                </tr>
            </thead>
            <tbody>
            {sortedEntries.map((entry, index) => {
              const isDriverTimeSheet = 'vehicle' in entry;
              const driverEntry = isDriverTimeSheet ? (entry as DriverTimeSheet) : null;

              return (
                <tr key={index}>
                    <td>
                        <Link to={`/events/${entry.event.id}`}>
                          {entry.event.event_name}
                        </Link>
                    </td>
                    <td>{getDateString(entry.start_date)}</td>
                    <td>{getTimeString(entry.start_date)}</td>
                    <td>{getTimeString(entry.end_date)}</td>
                    {hasDriverTimeSheets && (
                      <>
                        <td>{driverEntry?.vehicle ? `${driverEntry.vehicle.make} ${driverEntry.vehicle.model}` : '-'}</td>
                        <td>{driverEntry?.drive_time_to_location || '-'}</td>
                        <td>{driverEntry?.drive_time_from_location || '-'}</td>
                        <td>{driverEntry?.off_truck_time || '-'}</td>
                      </>
                    )}
                    <td>{entry.employee.first_name} {entry.employee.last_name}</td>
                </tr>
              );
            })}
            </tbody>
          </Table>
        </StackedContentContainer>
  )
}

export default DailyTimeCard
